import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import PlayArrowRounded from '@mui/icons-material/PlayArrowRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import BoltOutlined from '@mui/icons-material/BoltOutlined';
import PageHeader from '../components/common/PageHeader';
import { getBulkOps, createBulkOp, updateBulkOp, deleteBulkOp, runBulkOp } from '../services/bulkOpsApi';
import { getManufacturers } from '../services/manufacturersApi';
import { bulkSync } from '../services/syncApi';

const KIND_LABELS = {
  fabrics_to_products: 'Link fabrics → products',
  finishes_to_products: 'Link finishes → products',
};

const emptyRule = () => ({ source_manufacturer_ids: [], target_manufacturer_ids: [] });

const BulkOperations = () => {
  const [items, setItems] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [feedback, setFeedback] = useState(null);
  // After a bulk op finishes we collect the affected product ids and ask
  // whether to immediately Push them to stores. Empty array = no dialog.
  const [syncPrompt, setSyncPrompt] = useState({ open: false, productIds: [] });
  const [syncSubmitting, setSyncSubmitting] = useState(false);

  const [formName, setFormName] = useState('');
  const [formKind, setFormKind] = useState('fabrics_to_products');
  const [formSchedule, setFormSchedule] = useState('manual');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formRules, setFormRules] = useState([emptyRule()]);
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [opsData, mfgData] = await Promise.all([getBulkOps(), getManufacturers()]);
      setItems(opsData.items || []);
      setManufacturers(mfgData.items || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setEditItem(null);
    setFormName('');
    setFormKind('fabrics_to_products');
    setFormSchedule('manual');
    setFormEnabled(true);
    setFormRules([emptyRule()]);
    setFormError('');
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormName(item.name);
    setFormKind(item.kind);
    setFormSchedule(item.schedule);
    setFormEnabled(item.enabled);
    setFormRules((item.rules || []).length > 0
      ? item.rules.map((r) => ({
          source_manufacturer_ids: r.source_manufacturer_ids || [],
          target_manufacturer_ids: r.target_manufacturer_ids || [],
        }))
      : [emptyRule()]);
    setFormError('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) { setFormError('Name is required'); return; }
    const cleanRules = formRules
      .map((r) => ({
        source_manufacturer_ids: (r.source_manufacturer_ids || []).filter(Boolean),
        target_manufacturer_ids: (r.target_manufacturer_ids || []).filter(Boolean),
      }))
      .filter((r) => r.source_manufacturer_ids.length > 0 && r.target_manufacturer_ids.length > 0);
    if (cleanRules.length === 0) {
      setFormError('Add at least one rule with sources and targets');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        name: formName.trim(),
        kind: formKind,
        schedule: formSchedule,
        enabled: formEnabled,
        rules: cleanRules,
      };
      if (editItem) {
        await updateBulkOp(editItem.id, payload);
      } else {
        await createBulkOp(payload);
      }
      setDialogOpen(false);
      resetForm();
      await fetchData();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this operation? Links it created will remain.')) return;
    setDeleting(id);
    try {
      await deleteBulkOp(id);
      await fetchData();
    } finally {
      setDeleting(null);
    }
  };

  const handleRun = async (id) => {
    setRunning(id);
    setFeedback(null);
    try {
      const result = await runBulkOp(id);
      setFeedback({ severity: 'success', message: result.summary || 'Done' });
      await fetchData();
      const ids = Array.isArray(result.affectedProductIds) ? result.affectedProductIds : [];
      if (ids.length > 0) {
        setSyncPrompt({ open: true, productIds: ids });
      }
    } catch (err) {
      setFeedback({ severity: 'error', message: err.response?.data?.message || 'Run failed' });
    } finally {
      setRunning(null);
    }
  };

  const handleSyncAfterBulk = async () => {
    const productIds = syncPrompt.productIds;
    if (productIds.length === 0) {
      setSyncPrompt({ open: false, productIds: [] });
      return;
    }
    setSyncSubmitting(true);
    try {
      // includeUnlinked so newly-linked products that aren't on Shopify yet
      // get created (Push semantics), not silently skipped.
      const data = await bulkSync({ productIds, includeUnlinked: true });
      setFeedback({ severity: 'info', message: data.message || 'Sync started' });
    } catch (err) {
      setFeedback({ severity: 'error', message: err.response?.data?.message || 'Sync failed to start' });
    } finally {
      setSyncSubmitting(false);
      setSyncPrompt({ open: false, productIds: [] });
    }
  };

  const updateRule = (idx, patch) =>
    setFormRules((rules) => rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRule = () => setFormRules((rules) => [...rules, emptyRule()]);
  const removeRule = (idx) => setFormRules((rules) => rules.filter((_, i) => i !== idx));

  const mfgById = (id) => manufacturers.find((m) => m.id === id);

  const renderRuleSummary = (rule) => {
    const sources = (rule.source_manufacturer_ids || []).map((id) => mfgById(id)?.name).filter(Boolean);
    const targets = (rule.target_manufacturer_ids || []).map((id) => mfgById(id)?.name).filter(Boolean);
    return `${sources.join(', ') || '—'}  →  ${targets.join(', ') || '—'}`;
  };

  return (
    <Box>
      <PageHeader
        title="Bulk Operations"
        subtitle="Mass-link fabrics or finishes to products by manufacturer. Run manually or daily."
        action={
          <Button variant="contained" startIcon={<AddRounded />} onClick={openCreate}>
            Add Operation
          </Button>
        }
      />

      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>
          {feedback.message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <BoltOutlined sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No bulk operations yet</Typography>
            <Typography variant="body2" color="text.secondary">
              Click "Add Operation" to create your first one.
            </Typography>
          </Box>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((item) => (
            <Card key={item.id}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.name}</Typography>
                  <Chip size="small" label={KIND_LABELS[item.kind] || item.kind} />
                  <Chip
                    size="small"
                    label={item.schedule === 'daily' ? 'Daily 03:00 UTC' : 'Manual'}
                    color={item.schedule === 'daily' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                  {!item.enabled && <Chip size="small" label="Disabled" color="warning" variant="outlined" />}
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Run now">
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={running === item.id ? <CircularProgress size={14} /> : <PlayArrowRounded />}
                        onClick={() => handleRun(item.id)}
                        disabled={running !== null}
                      >
                        Run
                      </Button>
                    </span>
                  </Tooltip>
                  <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'primary.main' }}>
                    <EditRounded fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(item.id)} disabled={deleting === item.id} sx={{ color: 'error.main' }}>
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                  {(item.rules || []).map((rule) => (
                    <Typography key={rule.id} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: 'text.secondary' }}>
                      • {renderRuleSummary(rule)}
                    </Typography>
                  ))}
                  {(item.rules || []).length === 0 && (
                    <Typography variant="caption" color="text.secondary">No rules yet — edit to add.</Typography>
                  )}
                </Box>
                {item.last_run_at && (
                  <Typography variant="caption" color="text.secondary">
                    Last run: {new Date(item.last_run_at).toLocaleString()} · {item.last_run_status || '—'}
                    {item.last_run_summary ? ` · ${item.last_run_summary}` : ''}
                  </Typography>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editItem ? 'Edit Operation' : 'New Operation'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              sx={{ flex: 1 }}
              autoFocus
            />
            <FormControl sx={{ minWidth: 260 }}>
              <InputLabel>Kind</InputLabel>
              <Select value={formKind} onChange={(e) => setFormKind(e.target.value)} label="Kind">
                <MenuItem value="fabrics_to_products">Link fabrics → products</MenuItem>
                <MenuItem value="finishes_to_products">Link finishes → products</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Schedule</InputLabel>
              <Select value={formSchedule} onChange={(e) => setFormSchedule(e.target.value)} label="Schedule">
                <MenuItem value="manual">Manual only</MenuItem>
                <MenuItem value="daily">Daily (03:00 UTC)</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} />}
              label="Enabled"
            />
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Rules</Typography>
          {formRules.map((rule, idx) => (
            <Card variant="outlined" key={idx} sx={{ mb: 1.5, p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Autocomplete
                  multiple
                  size="small"
                  options={manufacturers}
                  getOptionLabel={(o) => o.name || ''}
                  value={manufacturers.filter((m) => rule.source_manufacturer_ids.includes(m.id))}
                  onChange={(_, val) => updateRule(idx, { source_manufacturer_ids: val.map((v) => v.id) })}
                  renderInput={(params) => (
                    <TextField {...params} label={formKind === 'fabrics_to_products' ? 'Source fabric manufacturers' : 'Source finish manufacturers'} />
                  )}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ pt: 1.5, color: 'text.secondary' }}>→</Box>
                <Autocomplete
                  multiple
                  size="small"
                  options={manufacturers}
                  getOptionLabel={(o) => o.name || ''}
                  value={manufacturers.filter((m) => rule.target_manufacturer_ids.includes(m.id))}
                  onChange={(_, val) => updateRule(idx, { target_manufacturer_ids: val.map((v) => v.id) })}
                  renderInput={(params) => <TextField {...params} label="Target product manufacturers" />}
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => removeRule(idx)} disabled={formRules.length === 1} sx={{ mt: 0.5 }}>
                  <CloseRounded fontSize="small" />
                </IconButton>
              </Box>
            </Card>
          ))}
          <Button size="small" startIcon={<AddRounded />} onClick={addRule} sx={{ textTransform: 'none' }}>
            Add line
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={16} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-run prompt: ask whether to immediately Push the products the
          bulk op touched. Uses Push semantics (includeUnlinked) so newly
          linked products that aren't on Shopify yet still get created. */}
      <Dialog open={syncPrompt.open} onClose={() => setSyncPrompt({ open: false, productIds: [] })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Sync linked products?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The bulk operation touched <strong>{syncPrompt.productIds.length}</strong> product
            {syncPrompt.productIds.length === 1 ? '' : 's'}. Push them to your Shopify stores now?
            This creates the Shopify product on stores that don't have it yet and updates the rest.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSyncPrompt({ open: false, productIds: [] })} disabled={syncSubmitting}>
            Not now
          </Button>
          <Button variant="contained" onClick={handleSyncAfterBulk} disabled={syncSubmitting}>
            {syncSubmitting ? <CircularProgress size={18} color="inherit" /> : 'Sync now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkOperations;
